/* eslint-disable react/jsx-newline */

import * as React from "react";
import styled from "styled-components";

import type { TabSetting } from "../../../../../../../../react-components/lu-component/src/index";
import { TabViewer } from "../../../../../../../../react-components/lu-component/src/index";
import { AdCopyRole } from "../../../../../../../../server/lib/ad-copy/permission/role";
import type { AccountWithoutPassword } from "../../../../../../../../server/models/account";
import {
	adCopyConceptTypeMapForQuota,
	type AdCopyTestOnlyConceptKey,
} from "../../../../../../../../server/models/ad-copy/concept";
import type { AdCopyCorrectionValue } from "../../../../../../../../server/models/ad-copy/simulation-value";
import type { ErrorObject } from "../../../../../../../../server/types/error";
import { AdCopyReportBigTitle } from "../../parts/report-title";
import { AdCopyCPPage } from "./cp";
import { AdCopySimulationOne } from "./simulation-one";
import type { GetAdCopySimulationV3Response } from "../../../../../../../../server/types/request/ad-copy/report/v3/simulation";
import { AdCopyCPPageProps } from "./cp";

const AreaWrap = styled.div`
	width: 1500px;
	margin: 0px auto;
	padding-left: 5px;
`;

const AlertRed = styled.span`
	display: inline-block;
	color: red;
	font-size: 12px;
`;

export type AdCopySimulationPageProps = GetAdCopySimulationV3Response & {
	confirmMessage: string | undefined;
	errors: ErrorObject | undefined;
	loginAccount: AccountWithoutPassword;
	onChangeRatio: <K extends keyof AdCopyCorrectionValue>(
		target: AdCopyTestOnlyConceptKey,
		name: K,
		value: AdCopyCorrectionValue[K],
	) => void;
	onClearCP: () => void;
	// simulation
	onSimulation: (conceptType: AdCopyTestOnlyConceptKey) => void;
	onSimulationSave: (conceptType: AdCopyTestOnlyConceptKey) => void;
	// cp
	onChangeCP: AdCopyCPPageProps["onChange"];
	onSubmitRecognitionRate: AdCopyCPPageProps["onSubmitRecognitionRate"];
	onSubmitPurchaseRate: AdCopyCPPageProps["onSubmitPurchaseRate"];
	refetch: () => void;
	setChanged: (changed: boolean) => void;
};

export const AdCopySimulationLatestPage = React.memo((props: AdCopySimulationPageProps) => {
	const {
		activity,
		confirmMessage,
		constant,
		currentProduct,
		errors,
		evaluation,
		goalSalesVolume,
		goalRecognitionRate,
		job,
		loginAccount,
		maxRecognition,
		referenceSourceData,
		onChangeCP,
		onChangeRatio,
		onClearCP,
		onSimulation,
		onSimulationSave,
		onSubmitPurchaseRate,
		onSubmitRecognitionRate,
		refetch,
		setChanged,
		simulationPredictionData,
	} = props;
	const hasEditRole = React.useMemo(
		() =>
			// 管理者 or ブランドメンバー or ブランドマネージャーは対象
			AdCopyRole.isAdmin(loginAccount) ||
			AdCopyRole.isActivityMember(loginAccount, activity) ||
			AdCopyRole.isActivityBrandManager(loginAccount, activity),
		[activity, loginAccount],
	);

	const canChangeCP = job != null;

	const tabs = React.useMemo<TabSetting[]>(() => {
		const tmp: TabSetting[] = [];

		if (simulationPredictionData.test1) {
			const targetConceptType = "test1";

			tmp.push({
				children: (
					<AdCopySimulationOne<typeof targetConceptType>
						activity={activity}
						constValue={constant}
						currentProduct={currentProduct}
						data={simulationPredictionData[targetConceptType]}
						evaluationResult={evaluation[targetConceptType]}
						goalRecognitionRate={goalRecognitionRate}
						goalSalesVolume={goalSalesVolume}
						hasEditRole={hasEditRole}
						job={job}
						maxRecognition={maxRecognition}
						referenceSourceData={referenceSourceData}
						onChangeRatio={(name, value) => {
							onChangeRatio(targetConceptType, name, value);
						}}
						onSimulation={() => {
							onSimulation(targetConceptType);
						}}
						onSimulationSave={() => {
							onSimulationSave(targetConceptType);
						}}
						targetConceptType={targetConceptType}
					/>
				),
				key: targetConceptType,
				title: adCopyConceptTypeMapForQuota[targetConceptType],
			});
		}

		if (simulationPredictionData.test2 && evaluation.test2) {
			const targetConceptType = "test2";

			tmp.push({
				children: (
					<AdCopySimulationOne<typeof targetConceptType>
						activity={activity}
						constValue={constant}
						currentProduct={currentProduct}
						data={simulationPredictionData[targetConceptType]}
						evaluationResult={evaluation[targetConceptType]}
						goalRecognitionRate={goalRecognitionRate}
						goalSalesVolume={goalSalesVolume}
						hasEditRole={hasEditRole}
						job={job}
						maxRecognition={maxRecognition}
						referenceSourceData={referenceSourceData}
						onChangeRatio={(name, value) => {
							onChangeRatio(targetConceptType, name, value);
						}}
						onSimulation={() => {
							onSimulation(targetConceptType);
						}}
						onSimulationSave={() => {
							onSimulationSave(targetConceptType);
						}}
						targetConceptType={targetConceptType}
					/>
				),
				key: targetConceptType,
				title: adCopyConceptTypeMapForQuota[targetConceptType],
			});
		}

		if (simulationPredictionData.test3 && evaluation.test3) {
			const targetConceptType = "test3";

			tmp.push({
				children: (
					<AdCopySimulationOne<typeof targetConceptType>
						activity={activity}
						constValue={constant}
						currentProduct={currentProduct}
						data={simulationPredictionData[targetConceptType]}
						evaluationResult={evaluation[targetConceptType]}
						goalRecognitionRate={goalRecognitionRate}
						goalSalesVolume={goalSalesVolume}
						hasEditRole={hasEditRole}
						job={job}
						maxRecognition={maxRecognition}
						referenceSourceData={referenceSourceData}
						onChangeRatio={(name, value) => {
							onChangeRatio(targetConceptType, name, value);
						}}
						onSimulation={() => {
							onSimulation(targetConceptType);
						}}
						onSimulationSave={() => {
							onSimulationSave(targetConceptType);
						}}
						targetConceptType={targetConceptType}
					/>
				),
				key: targetConceptType,
				title: adCopyConceptTypeMapForQuota[targetConceptType],
			});
		}

		if (currentProduct && canChangeCP) {
			tmp.push({
				children: (
					<AdCopyCPPage
						activity={activity}
						constValue={constant}
						currentProduct={currentProduct}
						data={simulationPredictionData}
						goalRecognitionRate={goalRecognitionRate}
						referenceSourceData={referenceSourceData}
						errors={errors}
						hasEditRole={hasEditRole}
						job={job}
						maxRecognition={maxRecognition}
						onChange={onChangeCP}
						onClear={onClearCP}
						onSubmitPurchaseRate={onSubmitPurchaseRate}
						onSubmitRecognitionRate={onSubmitRecognitionRate}
					/>
				),
				key: "cp",
				title: "実績値修正",
			});
		}

		return tmp;
	}, [
		activity,
		canChangeCP,
		constant,
		currentProduct,
		errors,
		evaluation,
		goalRecognitionRate,
		goalSalesVolume,
		hasEditRole,
		job,
		maxRecognition,
		onChangeCP,
		onChangeRatio,
		onClearCP,
		onSimulation,
		onSimulationSave,
		onSubmitPurchaseRate,
		onSubmitRecognitionRate,
		referenceSourceData,
		simulationPredictionData,
	]);

	return (
		<AreaWrap>
			<AdCopyReportBigTitle>
				補整＆提案フォーマット<AlertRed>（補整値は、アクティビティメンバーと承認者のみ入力可能です）</AlertRed>
				{job ? `（${job.jobNum}）` : ""}
			</AdCopyReportBigTitle>

			{tabs.length > 0 && (
				<TabViewer
					changed={!!confirmMessage}
					confirm={{
						body: confirmMessage ?? "",
						title: "保存確認",
					}}
					id="resultByTarget"
					onChangeTab={() => {
						setChanged(false);
						refetch();
					}}
					tabs={tabs}
				/>
			)}
		</AreaWrap>
	);
});
